import { useCallback, useEffect, useState } from 'react';

import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { useAuth } from '@contexts/auth';
import { useHeader } from '@contexts/header';
import { useScroll } from '@contexts/scroll';
import {
  DndContext,
  type DragEndEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDimensions } from '@hooks/useDimensions';
import { cn } from '@lib/ui/cn';
import { type IGetThemeContentOutputData } from '@services/majorAreas/dtos/getThemeContent/OutputData';
import MajorAreaService from '@services/majorAreas/MajorAreaService';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import Mark from 'mark.js';
import { useForm } from 'react-hook-form';
import {
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';

import OptionsButton from './options-button';

const formSchema = z.object({
  search: z.string(),
});

const markInstance = new Mark('#search-node');
let results: HTMLElement[] = [];
let currentIndex = 0;

function MenuLayout() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { onSetTitle, onSetTopics } = useHeader();
  const [searchParams] = useSearchParams();

  const { onScrollTo } = useScroll();
  const { height } = useDimensions();
  const [hasSearch, setHasSearch] = useState<boolean>(false);

  const [position, setPosition] = useState({
    x: 10,
    y: (height - 400) / 2,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      search: '',
    },
  });

  const { data } = useQuery<IGetThemeContentOutputData>({
    queryFn: async () => {
      const response = await MajorAreaService.getThemeContent({
        id: Number(params.topicId),
      });
      return response;
    },
    queryKey: ['themeContent', params.topicId],
    refetchOnWindowFocus: false,
  });

  function onSubmit(): void {
    //
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    setPosition((oldState) => {
      return {
        x: oldState.x - event.delta.x,
        y: oldState.y + event.delta.y,
      };
    });
  }

  const handleJumpTo = useCallback(() => {
    if (results.length) {
      (document.getElementById('back') as HTMLInputElement | null)!.disabled =
        false;
      (document.getElementById('next') as HTMLInputElement | null)!.disabled =
        false;
      document.getElementById('result')!.innerHTML =
        `${currentIndex + 1}/${results.length}`;
      const current = results[currentIndex];
      results.forEach((item) => {
        item.classList.remove('bg-orange-400');
      });
      if (current) {
        current.classList.add('bg-orange-400');
        onScrollTo(current.offsetTop - 60);
      }
    } else {
      (document.getElementById('back') as HTMLInputElement | null)!.disabled =
        true;
      (document.getElementById('next') as HTMLInputElement | null)!.disabled =
        true;
      document.getElementById('result')!.innerHTML = '';
    }
  }, [onScrollTo]);

  const handleNext = useCallback(() => {
    if (results.length) {
      currentIndex += 1;
      if (currentIndex < 0) {
        currentIndex = results.length - 1;
      }
      if (currentIndex > results.length - 1) {
        currentIndex = 0;
      }
      handleJumpTo();
    }
  }, [handleJumpTo]);

  const handleBack = useCallback(() => {
    if (results.length) {
      currentIndex += -1;
      if (currentIndex < 0) {
        currentIndex = results.length - 1;
      }
      if (currentIndex > results.length - 1) {
        currentIndex = 0;
      }
      handleJumpTo();
    }
  }, [handleJumpTo]);

  const handleCloseSearch = useCallback(() => {
    form.reset();
    setHasSearch(false);
    markInstance.unmark({
      done: function () {
        results = [];
        document.getElementById('result')!.innerHTML = '';
      },
    });
  }, [form]);

  const handleOpenSearch = useCallback(() => {
    setHasSearch(true);
    setTimeout(() => {
      (document.getElementById('back') as HTMLInputElement | null)!.disabled =
        true;
      (document.getElementById('next') as HTMLInputElement | null)!.disabled =
        true;
      form.setFocus('search');
    }, 500);
  }, [form]);

  useEffect(() => {
    const element = document.getElementById('search-node');
    const subscription = form.watch((value, { name }) => {
      if (name === 'search') {
        markInstance.unmark({
          done: function () {
            markInstance.mark(value.search || '', {
              separateWordSearch: true,
              done: function () {
                const array = element?.getElementsByTagName('mark').length
                  ? [...element.getElementsByTagName('mark')]
                  : [];
                results = array;
                handleJumpTo();
              },
            });
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, handleJumpTo]);

  useEffect(() => {
    if (data) {
      onSetTitle(data.title);
      onSetTopics(data.topics);
    }
  }, [data, onSetTitle, onSetTopics]);

  useEffect(() => {
    navigate(`${location.pathname}?${searchParams}`, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data && !data.isReleased && !user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      {hasSearch && (
        <Form {...form}>
          <form
            className={cn(
              'bg-background z-9 sticky top-0 pb-4 pt-2 transition-all',
            )}
            onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="search"
              render={({ field }) => (
                <FormItem className="bg-background flex flex-row items-center">
                  <div className="relative">
                    <FormControl>
                      <Input
                        className="text-foreground w-full max-w-64 [&_input]:pr-10"
                        placeholder="Buscar..."
                        {...field}
                      />
                    </FormControl>
                    <FormDescription
                      className="absolute right-3 top-2 font-light"
                      id="result"
                    />
                  </div>
                  <div>
                    <Button
                      className="size-7 cursor-pointer"
                      id="back"
                      onClick={handleBack}
                      size="icon"
                      type="button"
                      variant="ghost">
                      <ChevronUp className="size-5" />
                    </Button>
                    <Button
                      className="size-7 cursor-pointer"
                      id="next"
                      onClick={handleNext}
                      size="icon"
                      type="button"
                      variant="ghost">
                      <ChevronDown className="size-5" />
                    </Button>
                    <Button
                      className="size-7 cursor-pointer"
                      onClick={handleCloseSearch}
                      size="icon"
                      type="button"
                      variant="ghost">
                      <X className="size-5" />
                    </Button>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      )}
      <Outlet context={{ content: data?.content || '' }} />
      <OptionsButton
        onSearch={handleOpenSearch}
        position={{
          right: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </DndContext>
  );
}

export default MenuLayout;
